import {
    get_headers
} from '@/utils/constants/constants.js';
import {
    GET_SECTION_QUESTIONS
} from '@/utils/constants/constants.js';
import axios from 'axios';

export const questions_conditions_mixin = {
    data: () => ({
        section_questions_map: {},
        loading_questions: false,
        questions_loading_sections: new Set(),
        headers: get_headers()
    }),

    methods: {
        async load_sections_data() {
            const section_list = Reflect.get(this, 'section_list');
            if (!section_list || section_list.length === 0) {
                const load_sections_result = this.get_sections();
                return load_sections_result.catch(error => {
                    const error_message = 'Error loading sections data: ' + Reflect.get(error, 'message');
                    this.show_error(error_message);
                });
            }
        },

        async load_questions_for_selected_sections(condition) {
            const selected_sections_for_questions = this.ensure_array(
                Reflect.get(condition, 'selected_sections_for_questions')
            );

            if (selected_sections_for_questions.length === 0) {
                return;
            }

            this.initialize_questions_search_map(condition, selected_sections_for_questions);
            Reflect.set(this, 'loading_questions', true);
            await this.load_questions_for_all_sections(condition, selected_sections_for_questions);
            Reflect.set(this, 'loading_questions', false);
        },

        initialize_questions_search_map(condition, selected_sections_for_questions) {
            let questions_search_map = Reflect.get(condition, 'questions_search_map');
            if (!questions_search_map) {
                questions_search_map = {};
                Reflect.set(condition, 'questions_search_map', questions_search_map);
            }
            selected_sections_for_questions.forEach(section_id => {
                const search_value = Reflect.get(questions_search_map, section_id);
                if (!search_value) {
                    Reflect.set(questions_search_map, section_id, '');
                }
            });
        },

        async load_questions_for_all_sections(condition, selected_sections_for_questions) {
            const load_promises = selected_sections_for_questions.map(async section_id => {
                await this.load_questions_for_single_section(condition, section_id);
            });
            await Promise.all(load_promises);
        },

        async load_questions_for_single_section(condition, section_id) {
            const section_id_num = parseInt(section_id, 10);
            if (isNaN(section_id_num)) {
                return;
            }

            const existing_questions = Reflect.get(this.section_questions_map, section_id);
            if (existing_questions) {
                return;
            }

            const questions_loading_sections = Reflect.get(this, 'questions_loading_sections');
            questions_loading_sections.add(section_id);
            await this.fetch_section_questions(section_id_num);
            questions_loading_sections.delete(section_id);
        },

        async fetch_section_questions(section_id_num) {
            const form_id = this.get_form_id();
            const response = await axios.get(GET_SECTION_QUESTIONS, {
                params: {
                    form_id: form_id,
                    section_id: section_id_num
                },
                headers: Reflect.get(this, 'headers')
            });
            this.process_section_questions_response(response, section_id_num);
        },

        get_form_id() {
            const params = new URLSearchParams(window.location.search);
            const url_form_id = params.get('form_id');
            const form_id = url_form_id || sessionStorage.getItem('form_id');
            return form_id;
        },

        process_section_questions_response(response, section_id) {
            const response_data = this.parse_response_data(response);
            let questions_data = [];
            if (Reflect.get(response_data, 'status') === 'success') {
                questions_data = Reflect.get(response_data, 'data') || [];
            }
            Reflect.set(this.section_questions_map, section_id, questions_data);
            const updated_map = Object.assign({}, this.section_questions_map);
            Reflect.set(this, 'section_questions_map', updated_map);
        },

        parse_response_data(response) {
            const raw_data = Reflect.get(response, 'data');
            const parsed_data = typeof raw_data === "string" ? JSON.parse(raw_data) : raw_data;
            return parsed_data;
        },

        async load_questions_for_specific_scope_in_edit(condition) {
            const section_id = Reflect.get(condition, 'selected_section_for_questions');
            if (!section_id) {
                Reflect.set(condition, 'selected_questions', []);
                return;
            }

            Reflect.set(this, 'loading_questions', true);
            await this.load_questions_and_restore_selections(condition, section_id);
            Reflect.set(this, 'loading_questions', false);
        },

        async load_questions_and_restore_selections(condition, section_id) {
            const temp_condition = {
                selected_sections_for_questions: [section_id]
            };
            await this.load_questions_for_selected_sections(temp_condition);
            this.restore_selected_questions(condition, section_id);
        },

        restore_selected_questions(condition, section_id) {
            const selected_questions = Reflect.get(condition, 'selected_questions');
            if (!selected_questions || selected_questions.length === 0) {
                return;
            }

            const section_questions = Reflect.get(this.section_questions_map, section_id) || [];
            const valid_selected_questions = selected_questions.filter(q_id => {
                return section_questions.some(q => Reflect.get(q, 'id') === q_id);
            });
            Reflect.set(condition, 'selected_questions', valid_selected_questions);
        },

        toggle_select_all_section_questions(section, condition) {
            const section_questions = Reflect.get(section, 'questions');
            const section_question_ids = section_questions.map(question => Reflect.get(question, 'id'));
            const all_selected = this.are_all_section_questions_selected(section, condition);
            const selected_questions = Reflect.get(condition, 'selected_questions');

            if (all_selected) {
                const filtered_questions = selected_questions.filter(q_id => !section_question_ids.includes(q_id));
                Reflect.set(condition, 'selected_questions', filtered_questions);
            } else {
                const new_selections = section_question_ids.filter(q_id => !selected_questions.includes(q_id));
                selected_questions.push(...new_selections);
                Reflect.set(condition, 'selected_questions', selected_questions);
            }
        },

        are_all_section_questions_selected(section, condition) {
            const section_questions = Reflect.get(section, 'questions');
            if (!section_questions || section_questions.length === 0) {
                return false;
            }

            const selected_questions = Reflect.get(condition, 'selected_questions');
            const all_selected = section_questions.every(question => {
                return selected_questions.includes(Reflect.get(question, 'id'));
            });
            return all_selected;
        },

        select_all_questions(condition) {
            const filtered_questions = this.filtered_questions(condition);
            const all_question_ids = filtered_questions.map(question => Reflect.get(question, 'id'));
            const selected_questions = Reflect.get(condition, 'selected_questions');
            const combined_set = new Set();

            selected_questions.forEach(id => combined_set.add(id));
            all_question_ids.forEach(id => combined_set.add(id));

            const combined_questions = Array.from(combined_set);
            Reflect.set(condition, 'selected_questions', combined_questions);
        },

        deselect_all_questions(condition) {
            Reflect.set(condition, 'selected_questions', []);
        },

        get_selected_sections_count(condition) {
            const selected_questions = Reflect.get(condition, 'selected_questions');
            if (selected_questions.length === 0) {
                return 0;
            }

            const selected_sections_for_questions = Reflect.get(condition, 'selected_sections_for_questions');
            const sections_with_selections = new Set();

            selected_sections_for_questions.forEach(section_id => {
                const section_questions = this.get_questions_for_section(section_id) || [];
                const has_selected = section_questions.some(question => {
                    return selected_questions.includes(Reflect.get(question, 'id'));
                });
                if (has_selected) {
                    sections_with_selections.add(section_id);
                }
            });

            return sections_with_selections.size;
        },

        get_questions_for_section(section_id) {
            const section_id_num = parseInt(section_id, 10);
            const section_questions_map = this.section_questions_map;

            if (!section_questions_map || typeof section_questions_map !== 'object') {
                return [];
            }

            const questions_by_num = Reflect.get(section_questions_map, section_id_num);
            const questions_by_string = Reflect.get(section_questions_map, section_id);
            const questions = questions_by_num || questions_by_string || [];
            return questions;
        },

        is_section_loading(section_id) {
            const questions_loading_sections = Reflect.get(this, 'questions_loading_sections');
            return questions_loading_sections.has(section_id);
        },

        async on_sections_for_questions_change(condition) {
            const selected_sections_for_questions = Reflect.get(condition, 'selected_sections_for_questions');
            if (selected_sections_for_questions && selected_sections_for_questions.length > 0) {
                await this.load_questions_for_selected_sections(condition);
            } else {
                Reflect.set(condition, 'selected_questions', []);
            }
        },

        get_action_names_by_ids(action_ids) {
            const normalized_ids = this.normalize_ids(action_ids);
            if (Reflect.get(normalized_ids, 'length') === 0) {
                return [];
            }

            const actions_list = Reflect.get(this, 'actions_list');

            return normalized_ids
                .map(id => this.get_action_name_by_id(actions_list, id))
                .filter(name => name !== '');
        },

        normalize_ids(ids) {
            if (!ids) return [];
            if (Array.isArray(ids)) return ids;

            const wrapper = { value: ids };
            return [Reflect.get(wrapper, 'value')];
        },

        get_action_name_by_id(actions_list, id) {
            const found_action = actions_list.find(
                action => Reflect.get(action, 'id') === id
            );
            return found_action ? Reflect.get(found_action, 'name') : '';
        },

        get_sections_with_questions(condition) {
            const selected_sections_for_questions = this.ensure_array(
                Reflect.get(condition, 'selected_sections_for_questions')
            );

            if (selected_sections_for_questions.length === 0) {
                return [];
            }

            const sections_map = {};
            const section_list = Reflect.get(this, 'section_list');

            selected_sections_for_questions.forEach(section_id => {
                const section = this.find_section_by_id(section_list, section_id);
                if (section) {
                    const questions = this.get_questions_for_section(section_id) || [];
                    const section_data = Object.assign({}, section);
                    Reflect.set(section_data, 'questions', questions);
                    Reflect.set(section_data, 'isLoading', this.is_section_loading(section_id));
                    Reflect.set(sections_map, section_id, section_data);
                }
            });

            return Object.values(sections_map);
        },

        find_section_by_id(section_list, section_id) {
            const section = section_list.find(s => {
                return Reflect.get(s, 'id') === parseInt(section_id, 10);
            });
            return section;
        },

        get_question_display_text(question) {
            const question_text = Reflect.get(question, 'question_text');
            const text = Reflect.get(question, 'text');
            const name = Reflect.get(question, 'name');
            const id = Reflect.get(question, 'id');
            const display_text = question_text || text || name || `Question ${id}`;
            return display_text;
        },

        are_any_sections_loading(condition) {
            const selected_sections_for_questions = Reflect.get(condition, 'selected_sections_for_questions');
            if (!selected_sections_for_questions) {
                return false;
            }

            const any_loading = selected_sections_for_questions.some(section_id => {
                return this.is_section_loading(section_id);
            });
            return any_loading;
        },

        ensure_array(value) {
            if (!value) {
                return [];
            }
            if (Array.isArray(value)) {
                return value;
            }
            return [value];
        }
    }
};